// Header.js

import * as React from 'react';
import { useState, useEffect } from 'react';

// --- Utility Imports ---
import { authFetch } from '../authFetch'; 

// --- MUI Imports ---
import Stack from '@mui/material/Stack';
import Badge from '@mui/material/Badge';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box'; // <-- NEW: Added Box
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';

// --- NEW MUI Icons for Severity ---
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';

// --- Your Component Imports ---
import CustomDatePicker from './CustomDatePicker';
import NavbarBreadcrumbs from './NavbarBreadcrumbs';
import ColorModeIconDropdown from '../shared-theme/ColorModeIconDropdown';
import MenuButton from './MenuButton';

// Helper function to map risk levels to icons and colors
const getNotificationIcon = (level) => {
  switch (level) {
    case 'Extreme':
    case 'Severe':
      return <ErrorRoundedIcon color="error" />; // Red icon
    case 'High':
      return <WarningRoundedIcon color="warning" />; // Orange/Yellow icon
    default:
      return <InfoRoundedIcon color="info" />; // Blue icon for Moderate/Low/Updates
  }
};

export default function Header() {
  const [notifications, setNotifications] = useState([]);
  const[anchorEl, setAnchorEl] = useState(null);
  const [badgeCleared, setBadgeCleared] = useState(false);
  const open = Boolean(anchorEl);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await authFetch('/api/notifications/');
        const data = await response.json(); 
        setNotifications(data);

        if (data.some(n => !n.is_read)) {
          setBadgeCleared(false);
        }
      } catch (error) {
        console.error("Failed to fetch notifications:", error.message);
      }
    };
    fetchNotifications();
  },[]);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
    setBadgeCleared(true); // Clears the bell badge instantly
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMarkAsRead = async (notificationId) => {
    setNotifications(notifications.map(n => 
      n.id === notificationId ? { ...n, is_read: true } : n
    ));

    try {
      await authFetch(`/api/notifications/${notificationId}/mark_as_read/`, { method: 'POST' });
    } catch (error) {
      console.error("Failed to mark notification as read:", error.message);
      setNotifications(notifications.map(n => 
        n.id === notificationId ? { ...n, is_read: false } : n
      ));
    }
  };

  const displayBadgeCount = badgeCleared ? 0 : notifications.filter(n => !n.is_read).length;

  return (
    <Stack direction="row" spacing={2} sx={{ /* ... your existing header styles ... */ display: { xs: 'none', md: 'flex' }, width: '100%', alignItems: { xs: 'flex-start', md: 'center' }, justifyContent: 'space-between', maxWidth: { sm: '100%', md: '1700px' }, pt: 1.5 }}>
      <NavbarBreadcrumbs />
      <Stack direction="row" sx={{ gap: 1 }}>
        <CustomDatePicker />
        <MenuButton aria-label="Open notifications" onClick={handleMenuOpen}>
          <Badge badgeContent={displayBadgeCount} color="error">
            <NotificationsRoundedIcon />
          </Badge>
        </MenuButton>
        <ColorModeIconDropdown />
      </Stack>

      <Menu
        id="notifications-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        PaperProps={{ 
          // Set a max width and height so it looks like a clean dropdown panel
          style: { maxHeight: 500, width: '45ch', borderRadius: '12px', marginTop: '8px' } 
        }} 
      >
        {/* Header for the dropdown */}
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle1" fontWeight="bold">Notifications</Typography>
        </Box>

        {notifications.length > 0 ? (
          notifications.map((notification) => (
            <MenuItem 
              key={notification.id} 
              onClick={() => handleMarkAsRead(notification.id)}
              sx={{ 
                // ACTIONABLE STATE: Light blue if unread, transparent if read
                backgroundColor: notification.is_read ? 'transparent' : 'rgba(25, 118, 210, 0.04)',
                borderBottom: '1px solid',
                borderColor: 'divider',
                alignItems: 'flex-start',
                px: 2,
                py: 2,
                whiteSpace: 'normal',
                '&:hover': {
                    backgroundColor: notification.is_read ? 'action.hover' : 'rgba(25, 118, 210, 0.08)',
                }
              }}
            >
              <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                
                {/* 1. SEVERITY ICON */}
                <Box sx={{ mt: 0.25 }}>
                  {getNotificationIcon(notification.risk_level)} 
                </Box>

                {/* 2. TEXT CONTENT (Visual Hierarchy) */}
                <Stack spacing={0.5} sx={{ flex: 1 }}>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ fontWeight: notification.is_read ? 500 : 700 }}
                  >
                    {notification.subject}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ color: notification.is_read ? 'text.secondary' : 'text.primary', lineHeight: 1.4 }}
                  >
                    {notification.message}
                  </Typography>
                  
                  {/* Timestamp rendering */}
                  {notification.created_at && (
                    <Typography variant="caption" sx={{ color: 'text.disabled', mt: 0.5 }}>
                      {new Date(notification.created_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}
                    </Typography>
                  )}
                </Stack>

                {/* 3. UNREAD INDICATOR DOT */}
                {!notification.is_read && (
                  <Box sx={{ 
                    width: 8, height: 8, 
                    borderRadius: '50%', 
                    bgcolor: 'primary.main', 
                    mt: 1, 
                    flexShrink: 0 
                  }} />
                )}

              </Stack>
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled sx={{ py: 3, justifyContent: 'center' }}>
            <Typography variant="body2">No new notifications</Typography>
          </MenuItem>
        )}
      </Menu>
    </Stack>
  );
}